/* global axios */
import ApiClient from '../ApiClient';

// Match the reports API convention so calendar ranges use the viewer's day.
const getTimezoneOffset = () => -new Date().getTimezoneOffset() / 60;

class CaptainAssistant extends ApiClient {
  constructor() {
    super('captain/assistants', { accountScoped: true });
  }

  get({ page = 1, perPage = 25, searchKey, q } = {}) {
    return axios.get(this.url, {
      params: {
        page,
        per_page: perPage,
        q: q ?? searchKey,
      },
    });
  }

  publish({ assistantId, publicationMode, leadRolloutPercent }) {
    return axios.post(`${this.url}/${assistantId}/publish`, {
      publication_mode: publicationMode,
      lead_rollout_percent: leadRolloutPercent,
    });
  }

  unpublish({ assistantId }) {
    return axios.post(`${this.url}/${assistantId}/unpublish`);
  }

  playground({ assistantId, messageContent, messageHistory }) {
    return axios.post(`${this.url}/${assistantId}/playground`, {
      message_content: messageContent,
      message_history: messageHistory,
    });
  }

  getStats({ assistantId, range }) {
    return axios.get(`${this.url}/${assistantId}/stats`, {
      params: { range, timezone_offset: getTimezoneOffset() },
    });
  }

  getSummary({ assistantId, range }) {
    return axios.get(`${this.url}/${assistantId}/summary`, {
      params: { range, timezone_offset: getTimezoneOffset() },
    });
  }

  getDrilldown({ assistantId, metric, range, page, signal }) {
    const requestConfig = {
      params: {
        metric,
        range,
        timezone_offset: getTimezoneOffset(),
        page,
      },
    };

    if (signal) requestConfig.signal = signal;

    return axios.get(`${this.url}/${assistantId}/drilldown`, requestConfig);
  }

  actions({ assistantId }) {
    return axios.get(`${this.url}/${assistantId}/actions`);
  }

  runs({ assistantId, page = 1, perPage = 25 } = {}) {
    return axios.get(`${this.url}/${assistantId}/runs`, {
      params: { page, per_page: perPage },
    });
  }

  run({ assistantId, runId }) {
    return axios.get(`${this.url}/${assistantId}/runs/${runId}`);
  }

  cancelRun({ assistantId, runId }) {
    return axios.post(`${this.url}/${assistantId}/runs/${runId}/cancel`);
  }

  observability({ assistantId, windowMinutes = 1440, includeEval = true }) {
    return axios.get(`${this.url}/${assistantId}/runs/observability`, {
      params: {
        window_minutes: windowMinutes,
        include_eval: includeEval,
      },
    });
  }

  replayPlan({ assistantId, runId }) {
    return axios.post(`${this.url}/${assistantId}/runs/${runId}/replay_plan`);
  }

  handoffPlan({ assistantId, runId }) {
    return axios.post(`${this.url}/${assistantId}/runs/${runId}/handoff_plan`);
  }

  memories({ assistantId, page = 1, perPage = 25 } = {}) {
    return axios.get(`${this.url}/${assistantId}/memories`, {
      params: { page, per_page: perPage },
    });
  }

  createMemory({ assistantId, memory }) {
    return axios.post(`${this.url}/${assistantId}/memories`, { memory });
  }

  updateMemory({ assistantId, memoryId, memory }) {
    return axios.patch(`${this.url}/${assistantId}/memories/${memoryId}`, {
      memory,
    });
  }

  deleteMemory({ assistantId, memoryId }) {
    return axios.delete(`${this.url}/${assistantId}/memories/${memoryId}`);
  }

  promptVersions({
    assistantId,
    scenarioId,
    sourceType,
    page = 1,
    perPage = 25,
  }) {
    return axios.get(`${this.url}/${assistantId}/prompt_versions`, {
      params: {
        scenario_id: scenarioId,
        source_type: sourceType,
        page,
        per_page: perPage,
      },
    });
  }

  approvals({ assistantId, status = 'pending', page = 1, perPage = 25 } = {}) {
    return axios.get(`${this.baseUrl()}/captain/agent_approvals`, {
      params: {
        assistant_id: assistantId,
        status,
        page,
        per_page: perPage,
      },
    });
  }

  approveAction({ approvalId, decisionPayload = {} }) {
    return axios.post(
      `${this.baseUrl()}/captain/agent_approvals/${approvalId}/approve`,
      { decision_payload: decisionPayload }
    );
  }

  rejectAction({ approvalId, decisionPayload = {} }) {
    return axios.post(
      `${this.baseUrl()}/captain/agent_approvals/${approvalId}/reject`,
      { decision_payload: decisionPayload }
    );
  }
}

export default new CaptainAssistant();
